import React, { useState, useEffect, useRef } from 'react';
import { UserIcon } from '@heroicons/react/24/outline';
import { providerService } from '../services/provider';

export interface Provider {
  id: string;
  name: string;
  email: string;
  role: 'provider' | 'admin';
  isSelf: boolean;
}

interface ProviderSearchProps {
  onSelectProvider: (provider: Provider | null) => void;
  selectedProviderId?: string;
  placeholder?: string;
  label?: string;
  required?: boolean;
}

export const ProviderSearch: React.FC<ProviderSearchProps> = ({
  onSelectProvider,
  selectedProviderId,
  placeholder = 'Search for a provider...',
  label = 'Provider',
  required = false
}) => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [filteredProviders, setFilteredProviders] = useState<Provider[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [loading, setLoading] = useState(false);
  const [canAssignToOthers, setCanAssignToOthers] = useState(true);
  const searchRef = useRef<HTMLDivElement>(null);

  // Fetch providers when component mounts
  useEffect(() => {
    const fetchProviders = async () => {
      setLoading(true);
      try {
        const data = await providerService.getSimpleProviders();
        setProviders(data.providers);
        setFilteredProviders(data.providers);
        setCanAssignToOthers(data.canAssignToOthers);
      } catch (error) {
        console.error('Error fetching providers:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProviders();
  }, []);

  // Set selected provider when selectedProviderId changes
  useEffect(() => {
    if (selectedProviderId && (providers?.length || 0) > 0) {
      const provider = (providers || []).find(p => p.id === selectedProviderId);
      if (provider) {
        setSelectedProvider(provider);
        setSearchTerm(provider.name);
      }
    } else {
      setSelectedProvider(null);
      setSearchTerm('');
    }
  }, [selectedProviderId, providers]);

  // Filter providers based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProviders(providers || []);
    } else {
      const filtered = (providers || []).filter(provider =>
        provider.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        provider.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        provider.role.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProviders(filtered);
    }
  }, [searchTerm, providers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        // Reset search term to selected provider name if no selection made
        if (selectedProvider) {
          setSearchTerm(selectedProvider.name);
        } else {
          setSearchTerm('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedProvider]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsOpen(true);

    // Clear selection if search term doesn't match any provider
    if (selectedProvider && !selectedProvider.name.toLowerCase().includes(value.toLowerCase())) {
      setSelectedProvider(null);
      onSelectProvider(null);
    }
  };

  const handleProviderSelect = (provider: Provider) => {
    setSelectedProvider(provider);
    setSearchTerm(provider.name);
    setIsOpen(false);
    onSelectProvider(provider);
  };

  const handleClearSelection = () => {
    setSelectedProvider(null);
    setSearchTerm('');
    onSelectProvider(null);
    setIsOpen(false);
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const getProviderDisplayText = (provider: Provider) => {
    const selfText = provider.isSelf ? ' (You)' : '';
    const roleText = provider.role === 'admin' ? ' • Admin' : '';
    return `${provider.name}${selfText}${roleText}`;
  };

  return (
    <div className="relative" ref={searchRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      
      <div className="relative">
        <input
          type="text"
          className="input-field pr-10"
          placeholder={loading ? 'Loading providers...' : placeholder}
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={handleFocus}
          disabled={loading || (!canAssignToOthers && (providers?.length || 0) <= 1)}
          required={required}
        />
        
        <div className="absolute inset-y-0 right-0 flex items-center">
          {selectedProvider && (
            <button
              type="button"
              onClick={handleClearSelection}
              className="p-1 text-gray-400 hover:text-gray-600 mr-2"
              title="Clear selection"
            >
              ×
            </button>
          )}
          <div className="p-2">
            <UserIcon className="h-4 w-4 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && !loading && (filteredProviders?.length || 0) > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {(filteredProviders || []).map((provider) => (
            <button
              key={provider.id}
              type="button"
              onClick={() => handleProviderSelect(provider)}
              className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                selectedProvider?.id === provider.id ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">
                    {getProviderDisplayText(provider)}
                  </div>
                  <div className="text-sm text-gray-500">{provider.email}</div>
                </div>
                {provider.isSelf && (
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                    You
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No results message */}
      {isOpen && !loading && searchTerm && (filteredProviders?.length || 0) === 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg p-4 text-center text-gray-500">
          No providers found matching "{searchTerm}"
        </div>
      )}

      {/* Loading message */}
      {loading && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg p-4 text-center text-gray-500">
          Loading providers...
        </div>
      )}

      {/* No assignment permission message */}
      {!canAssignToOthers && (providers?.length || 0) > 1 && (
        <p className="text-xs text-gray-500 mt-1">
          You can only assign appointments to yourself
        </p>
      )}

      {/* Selected provider info */}
      {selectedProvider && (
        <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
          <span className="font-medium">Selected:</span> {getProviderDisplayText(selectedProvider)}
        </div>
      )}
    </div>
  );
};